// ============================
// genericZustandSlice.ts
// ============================
import { StateCreator } from 'zustand';

export type CapitalizeString<T> = T extends `${infer F}${infer R}` ? `${Uppercase<F>}${R}` : T;

export type GenericSlice<T> = {
  state: T | null;
  setup: (data: T) => void;
} & {
  [K in keyof T as `update${CapitalizeString<string & K>}`]: (value: T[K]) => void;
};

export function createGenericSlice<T extends object>(
  ClassRef: new () => T,
  stateKey: string = 'state',
  setupMethod: string = 'setup',
  customMethods?: (set: any, get: any) => Partial<Record<string, any>>
): StateCreator<GenericSlice<T> & Record<string, any>> {
  const dummy = new ClassRef();
  return (set, get) => {
    const updateField = <K extends keyof T>(key: K, value: T[K]) => {
      set((state: any) => {
        const current = state[stateKey];
        return current ? { [stateKey]: { ...current, [key]: value } } : state;
      });
    };

    const methods: Partial<GenericSlice<T>> = {};

    
    for (const key of Object.keys(dummy) as (keyof T)[]) {
      if (typeof key === 'string') {
        const methodName = `update${key.charAt(0).toUpperCase()}${key.slice(1)}`;
        (methods as any)[methodName] = (value: any) => updateField(key, value);
      }
    }

    return {
      [stateKey]: null,
      [setupMethod]: (data: T) => { set({ [stateKey]: { ...data } } as Partial<GenericSlice<T>>) },
      ...methods,
      ...(customMethods ? customMethods(set, get) : {}),
    } as GenericSlice<T> & Record<string, any>;
  };
}



